import cn from "classnames";
import { handleBack } from "core-lib/utils/common";
import { useMemo } from "react";
import BackButton from "ui-v2/components/BackButton";
import { Typography } from "ui-v2/components/Typography";
import ArrowLeftRegular from "ui-v2/icons/ArrowLeftRegular";
import ChevronDownRegular from "ui-v2/icons/ChevronDownRegular";
import { semanticColors, utilityColors } from "ui-v2/tokens/colors";
import { SemanticBgLevels, UtilityLevels } from "ui-v2/types/colors";
import s from "./AppBar.module.scss";

export type AppBarProps = {
  zIndex?: number;
  isTransparent?: boolean;
  backButton?: {
    onClick?: () => void;
    show?: boolean;
  };
  withSafeArea?: boolean;
  title?: {
    label?: string;
    onClick?: () => void;
  };
  subtitle?: {
    label?: string;
    onClick?: () => void;
    showIcon?: boolean;
  };
  actions?: {
    children?: React.ReactElement<HTMLOrSVGElement>;
    onClick?: () => void;
  }[];
  withBottomLine?: boolean;
  bgColor?: SemanticBgLevels | UtilityLevels;
  blurredBg?: boolean;
  onDark?: boolean;
};

/**
 * AppBar
 * Below are the list of props accepted
 *
 * @param zIndex will be a number to increase z index of app bar
 * @param isTransparent an boolean indicating whether the app bar is transparent
 * @param backButton an object with click handler event
 * @param withSafeArea a boolean indicating whether the app bar has safe area
 * @param title an object with label of string type
 * @param subtitle an object with label of string type, a click handler event and showIcon boolean
 * @param actions an array of objects with children and click handler events, children might be a html or svg element
 * @param withBottomLine a boolean indicating whether the app bar has bottom line aka divider
 * @param bgColor a background color for the app bar
 * @param blurredBg a boolean indicating whether the app bar has blurred
 * @param onDark a boolean indicating whether the app bar theme dark mode switches all icons ant text to light colors
 */

export const AppBar = ({
  zIndex = 0,
  isTransparent = false,
  backButton = {
    onClick: () => handleBack(),
    show: true,
  },
  withSafeArea = true,
  title = {
    label: "",
  },
  subtitle = {
    label: "",
    showIcon: false,
  },
  actions = [],
  withBottomLine = false,
  bgColor = "primary",
  blurredBg = false,
  onDark = false,
}: AppBarProps) => {
  const backgroundColor = useMemo(() => {
    if (isTransparent) return "transparent";
    else if (blurredBg && bgColor) {
      return semanticColors?.bg?.[`${bgColor}`]
        ? `${semanticColors?.bg?.[`${bgColor}`]}70`
        : `${utilityColors?.bg?.[`${bgColor}`]}70`;
    } else if (bgColor) {
      return semanticColors?.bg?.[`${bgColor}`]
        ? `${semanticColors?.bg?.[`${bgColor}`]}`
        : `${utilityColors?.bg?.[`${bgColor}`]}`;
    }
  }, [isTransparent, blurredBg, bgColor]);

  return (
    <div
      className={cn(s.root, {
        [s.withBorder]: withBottomLine,
        [s.noSafeArea]: !withSafeArea,
        [s.blurredBg]: blurredBg,
      })}
      style={{
        paddingTop: withSafeArea ? "calc(8px + var(--safe-area-top))" : "8px",
        zIndex: zIndex,
        backgroundColor: backgroundColor,
        minHeight: withSafeArea ? " calc(56px + var(--safe-area-top))" : "56px",
      }}
    >
      <div className={s.rightPanel}>
        {backButton?.show && (
          <BackButton
            onBack={() => {
              backButton.onClick();
            }}
            className={s.backButton}
          >
            <ArrowLeftRegular color={onDark ? "on_dark_bg" : "primary"} />
          </BackButton>
        )}
        <div className={s.textStack}>
          {title?.label && (
            <div onClick={title?.onClick}>
              <Typography
                label={title?.label}
                variant="label_2_bold"
                color={onDark ? "on_dark_bg" : "primary"}
                className={s.title}
              />
            </div>
          )}
          {subtitle?.label && (
            <div className={s.subtitle} onClick={subtitle?.onClick}>
              <Typography
                label={subtitle?.label}
                variant="label_4_medium"
                color={onDark ? "on_dark_bg" : "tertiary"}
              />
              {subtitle?.showIcon && (
                <ChevronDownRegular
                  color={onDark ? "on_dark_bg" : "primary"}
                  width={16}
                  height={16}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {!!actions?.length && (
        <div className={s.actions}>
          {actions?.map((item, idx) => (
            <div key={`${idx}+1`} onClick={item?.onClick}>
              {item?.children}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
